import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";

const defaultSnoozeTime = () => {
  const date = new Date(Date.now() + 16 * 60 * 1000);
  const offset = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const toReminderPayload = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toISOString();
};

export const ReminderSnoozeDialog = ({ notification, onClose, onSnoozed }) => {
  const [reminderAt, setReminderAt] = useState(defaultSnoozeTime);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (notification) setReminderAt(defaultSnoozeTime());
  }, [notification]);

  if (!notification) return null;
  const save = async () => {
    if (!reminderAt) {
      toast.error("Choose a new reminder date and time");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/technician-tasks/${notification.data.task_id}/snooze`, {
        notification_id: notification.id,
        reminder_at: toReminderPayload(reminderAt),
      });
      onSnoozed(notification.id, data.reminder_at);
      toast.success("Reminder snoozed");
      onClose();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-sm" data-testid="reminder-snooze-dialog">
        <DialogHeader>
          <DialogTitle>Snooze reminder</DialogTitle>
          <DialogDescription className="sr-only">
            Set a new local date and time for this personal to-do reminder.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="reminder-snooze-time-input">Remind me at</Label>
          <Input
            id="reminder-snooze-time-input"
            min={defaultSnoozeTime()}
            onChange={(event) => setReminderAt(event.target.value)}
            type="datetime-local"
            value={reminderAt}
            data-testid="reminder-snooze-time-input"
          />
          <p className="text-xs text-muted-foreground" data-testid="reminder-snooze-time-help">
            Choose any future time in your device&apos;s local time.
          </p>
        </div>
        <DialogFooter className="gap-2">
          <Button
            disabled={saving}
            onClick={onClose}
            type="button"
            variant="outline"
            data-testid="reminder-snooze-cancel-button"
          >
            Cancel
          </Button>
          <Button
            disabled={saving}
            onClick={save}
            data-testid="reminder-snooze-save-button"
          >
            {saving ? "Snoozing…" : "Snooze reminder"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};